import { create } from 'zustand';
import { customPersist } from '@/utils/customStorage.ts';
import { RenderingEngine } from '@/type/renderMarkdown.ts';
import { MarkdownRendererProps } from '@noedgeai/markdown-renderer/react';

export enum IsTheWindowsPopUpClosed {
  NONE = 0,
  MINIMIZE,
  MinimizeToTray,
  CLOSE,
}
interface UserSettingsState {
  isTheWindowsPopUpClosed: IsTheWindowsPopUpClosed;
  setIsTheWindowsPopUpClosed: (
    isTheWindowsPopUpClosed: IsTheWindowsPopUpClosed,
  ) => void;
  // 用户解析完成后是否自动进入详情
  enterDetailAfterParsed: boolean;
  // 更新状态的方法
  setEnterDetailAfterParsed: (value: boolean) => void;
  // 解析增强配置
  analyticalEnhancement: boolean;
  // 更新解析增强配置的方法
  setAnalyticalEnhancement: (value: boolean) => void;
  // 下次不弹出弹窗
  noPopUpWindowPopsUpNextTime: boolean;
  setNoPopUpWindowPopsUpNextTime: (value: boolean) => void;
  // 新增的渲染引擎属性
  renderingEngine: RenderingEngine;
  // 更新渲染引擎的方法
  setRenderingEngine: (value: RenderingEngine) => void;
  formulaRenderer: MarkdownRendererProps['renderer'];
  setFormulaRenderer: (value: MarkdownRendererProps['renderer']) => void;
  // 自动保存开关
  autoSaveEnabled: boolean;
  setAutoSaveEnabled: (value: boolean) => void;
  // 图片解析引导是否已完成
  imageParseGuideCompleted: boolean;
  setImageParseGuideCompleted: (value: boolean) => void;
  // 新手引导完成状态
  hasCompletedGuide: boolean;
  setHasCompletedGuide: (value: boolean) => void;
  // 是否正在进行新手引导
  isInOnboarding: boolean;
  setIsInOnboarding: (value: boolean) => void;
  pandocPath: string;
  setPandocPath: (value: string) => void;
  whetherTheDesktopHasJumpedToSettings: boolean;
  setWhetherTheDesktopHasJumpedToSettings: (value: boolean) => void;
  shareOthersVipShow: boolean;
  setShareOthersVipShow: (value: boolean) => void;
}

const useUserSettingsStore = create<UserSettingsState>()(
  customPersist(
    (set) => ({
      isTheWindowsPopUpClosed: IsTheWindowsPopUpClosed.NONE,
      setIsTheWindowsPopUpClosed: (value) =>
        set({ isTheWindowsPopUpClosed: value }),
      enterDetailAfterParsed: true, // 默认状态为 true
      setEnterDetailAfterParsed: (value: boolean) =>
        set({ enterDetailAfterParsed: value }),
      analyticalEnhancement: true, // 默认解析增强配置为 true
      setAnalyticalEnhancement: (value: boolean) =>
        set({ analyticalEnhancement: value }),
      noPopUpWindowPopsUpNextTime: false,
      setNoPopUpWindowPopsUpNextTime: (value: boolean) =>
        set({ noPopUpWindowPopsUpNextTime: value }),
      renderingEngine: 'virtualView', // 默认渲染引擎为 virtualView
      setRenderingEngine: (value: RenderingEngine) =>
        set({ renderingEngine: value }),

      formulaRenderer: 'mathjax',
      setFormulaRenderer: (value: MarkdownRendererProps['renderer']) =>
        set({ formulaRenderer: value }),
      autoSaveEnabled: true, // 默认开启自动保存
      setAutoSaveEnabled: (value: boolean) => set({ autoSaveEnabled: value }),
      imageParseGuideCompleted: false, // 默认未完成引导
      setImageParseGuideCompleted: (value: boolean) =>
        set({ imageParseGuideCompleted: value }),
      hasCompletedGuide: false, // 默认未完成新手引导
      setHasCompletedGuide: (value: boolean) =>
        set({ hasCompletedGuide: value }),
      isInOnboarding: false, // 默认未在新手引导中
      setIsInOnboarding: (value: boolean) => set({ isInOnboarding: value }),
      pandocPath: 'pandoc',
      setPandocPath: (value: string) => set({ pandocPath: value }),
      whetherTheDesktopHasJumpedToSettings: false,
      setWhetherTheDesktopHasJumpedToSettings: (value: boolean) =>
        set({ whetherTheDesktopHasJumpedToSettings: value }),
      shareOthersVipShow: true,
      setShareOthersVipShow: (value: boolean) =>
        set({ shareOthersVipShow: value }),
    }),
    {
      name: 'user-settings', // 存储在 localStorage 中的 key
      version: 8,
    },
  ),
);

export default useUserSettingsStore;
